import { Query, QueryStringParams } from '@/src/interfaces'
import { ReadonlyURLSearchParams } from 'next/navigation'

/**
 * Extracts and updates query parameters from URL search parameters.
 *
 * This function takes previous query and query parameters, and the current
 * URL search parameters, and returns updated query and query parameters
 * based on matches found in the URL.
 *
 * @param {Query} prevQuery - The previous query object.
 * @param {QueryStringParams} prevQueryParams - The previous query parameters.
 * @param {ReadonlyURLSearchParams} urlParams - The URL search parameters.
 * @returns {Object} An object containing the updated query and query parameters.
 */
export const useQueryString = (
  prevQuery: Query,
  prevQueryParams: QueryStringParams,
  urlParams: ReadonlyURLSearchParams
): { query: Query; queryParams: QueryStringParams } => {
  if (!urlParams) {
    return { query: prevQuery, queryParams: prevQueryParams }
  }

  const singleKeys = [
    'specialty',
    'disease_state',
    'unified',
    'full_name',
  ] as const
  const checkboxKeys = [
    'booking_url',
    'telehealth_appointment',
    'accepts_new_patients',
  ] as const
  const locationKeys = ['state', 'city', 'zip'] as const

  const updatedQuery: Query = {
    ...prevQuery,
  }

  const updatedQueryParams: QueryStringParams = { ...prevQueryParams }

  // Single value match (only one active key expected)
  for (const key of singleKeys) {
    const value = urlParams.get(key)
    if (value) {
      updatedQuery.singleValueParameters = { key, value }
      break
    }
  }

  // Location
  updatedQuery.locationParameters = {}
  for (const key of locationKeys) {
    const value = urlParams.get(key)
    if (value) {
      updatedQuery.locationParameters[key] = value
    }
  }

  // Multi-value fields
  updatedQuery.insuranceParameters = urlParams.get('insurance') || ''
  updatedQuery.genderParameters = urlParams.get('gender') || ''
  updatedQuery.languageParameters = urlParams.get('language') || ''

  // Checkbox fields
  for (const key of checkboxKeys) {
    updatedQuery.checkBoxedParameters[key] = urlParams.get(key) === 'true'
  }

  // Geo fields
  const distance = urlParams.get('distance')
  const lat = parseFloat(urlParams.get('lat') || '')
  const lon = parseFloat(urlParams.get('lon') || '')

  if (distance && !isNaN(lat) && !isNaN(lon)) {
    updatedQuery.geoDistanceParameters = {
      distance,
      currentLocation: { lat, lon },
    }
  }

  // Add generic query string parameters
  for (const key of Object.keys(prevQueryParams)) {
    const value = urlParams.get(key)
    if (value !== null) {
      updatedQueryParams[key] = value
    }
  }

  return {
    query: { ...prevQuery, ...updatedQuery },
    queryParams: updatedQueryParams,
  }
}
